'use client';

import type { CSSProperties, FC } from 'react';
import Image, { type ImageProps } from 'next/image';

// Uniform Image Delivery API transformation parameters
interface UniformTransformParams {
  fit?: 'scale-down' | 'contain' | 'cover';
  focal?: 'auto' | 'center' | `${number}x${number}`; // NxM format where N and M are 0.0-1.0
}

// Scene7 Image Serving API transformation parameters
interface Scene7TransformParams {
  fit?: 'fit' | 'constrain' | 'crop' | 'wrap' | 'stretch' | 'hfit' | 'vfit';
  fmt?: 'jpg' | 'png' | 'png-alpha' | 'gif' | 'webp';
}

// Uniform and Scene7-specific options
interface ImgProps extends Omit<ImageProps, 'src' | 'loader'> {
  src: string;
  uniformTransform?: UniformTransformParams;
  scene7Transform?: Scene7TransformParams;
  className?: string;
  style?: CSSProperties;
}

// Helper to check if URL is from Scene7
const isScene7Url = (src: string): boolean => src.includes('scene7.com');

// Helper to check if URL is from Uniform
const isUniformUrl = (src: string): boolean =>
  src.includes('img.uniform.global');

// Custom loader for Uniform images
const uniformImageLoader = ({
  src,
  width,
  height,
}: {
  src: string;
  width: number;
  height?: number;
  quality?: number;
}) => {
  if (isUniformUrl(src)) {
    const url = new URL(src);

    // Apply width and height from Next.js
    url.searchParams.set('width', width.toString());
    if (height) {
      url.searchParams.set('height', height.toString());
    }

    return url.toString();
  }

  return src;
};

// Custom loader for Scene7 images
const scene7ImageLoader = ({
  src,
  width,
  height,
  quality,
}: {
  src: string;
  width: number;
  height?: number;
  quality?: number;
}) => {
  if (isScene7Url(src)) {
    const url = new URL(src);

    url.searchParams.set('wid', width.toString());
    if (height) {
      url.searchParams.set('hei', height.toString());
    }

    if (quality) {
      url.searchParams.set('qlt', quality.toString());
    }

    return url.toString();
  }

  return src;
};

const Img: FC<ImgProps> = ({
  src,
  uniformTransform,
  scene7Transform,
  style,
  ...imageProps
}) => {
  const isUniform = isUniformUrl(src);
  const isScene7 = isScene7Url(src);

  let finalSrc = src;

  // Apply Uniform transformations
  if (isUniform && uniformTransform) {
    const url = new URL(src);

    if (uniformTransform.fit) {
      url.searchParams.set('fit', uniformTransform.fit);
    }

    if (uniformTransform.focal) {
      url.searchParams.set('focal', uniformTransform.focal);
    }

    finalSrc = url.toString();
  }

  // Apply Scene7 transformations
  if (isScene7 && scene7Transform) {
    const url = new URL(src);

    if (scene7Transform.fit) {
      url.searchParams.set('fit', scene7Transform.fit);
    }

    if (scene7Transform.fmt) {
      url.searchParams.set('fmt', scene7Transform.fmt);
    }

    finalSrc = url.toString();
  }

  // Determine which loader to use
  const getLoader = () => {
    if (isUniform) return uniformImageLoader;
    if (isScene7) return scene7ImageLoader;
    return undefined;
  };

  return (
    <Image src={finalSrc} loader={getLoader()} style={style} {...imageProps} />
  );
};

export default Img;
